import {Box} from "@mui/material";
import {ColumnDef} from "@tanstack/react-table";
import {AgendaV3} from "crqlar-react-common/dist/models/agenda/agenda.model";
import {AgendaService} from "crqlar-react-common/dist/services/agenda/agenda.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import SettingsListPage from "../layout/SettingsListPage";

const AgendaList = () => {
    useTrackScreenVisit('agenda list');

    const navigate = useNavigate();
    const {t} = useTranslation();

    const columns = useMemo<ColumnDef<AgendaV3>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: agenda => agenda.name,
            cell: ({row}) => <Box>{row.original.name ?? ''}</Box>,
            size: 320,
            meta: {defaultActive: true, isServerSortable: true, grow: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: agenda => agenda.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: agenda => agenda.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);

    const controller = useServerTable({
        tableId: 'system-agenda',
        columns,
        getRowId: agenda => agenda.id!,
        onRowClick: agenda => navigate(`/system/agenda/${agenda.id}`),
        initialSorting: [{id: 'name', desc: false}],
        queryFn: async ({pagination, searchQuery, sorting}) => {
            const page = await AgendaService.getAllAgendas({
                name: searchQuery ? [searchQuery] : undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });
            return {content: page.content ?? [], totalElements: page.totalElements ?? 0};
        }
    });

    return (
        <SettingsListPage title={t('Agenda')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                        <ServerTableCreate
                            label={t('Create Agenda')}
                            onClick={() => navigate('/system/agenda/new')}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default AgendaList;
